import { createContext, ReactNode, useCallback, useContext, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { HelpCenterContextValue } from "../types"

const HelpCenterContext = createContext<HelpCenterContextValue | null>(null)

interface HelpCenterProviderProps {
  children: ReactNode
}

const SCROLL_OFFSET = 100 // Offset for sticky header

export function HelpCenterProvider(props: HelpCenterProviderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")

  const navigateToSection = useCallback(
    (anchorId: string, path?: string) => {
      if (path && path !== location.pathname) {
        navigate(`${path}#${anchorId}`)
        return
      }

      const element = document.getElementById(anchorId)
      if (!element) return

      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - SCROLL_OFFSET

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      })

      window.history.replaceState(null, "", `${location.pathname}#${anchorId}`)
    },
    [navigate, location.pathname]
  )

  const value: HelpCenterContextValue = {
    activeSectionId,
    setActiveSectionId,
    searchQuery,
    setSearchQuery,
    currentTopicPath: location.pathname,
    navigateToSection
  }

  return (
    <HelpCenterContext.Provider value={value}>
      {props.children}
    </HelpCenterContext.Provider>
  )
}

export function useHelpCenterContext(): HelpCenterContextValue {
  const context = useContext(HelpCenterContext)
  if (!context) {
    throw new Error("useHelpCenterContext must be used within HelpCenterProvider")
  }
  return context
}
